import { buildProductTourFacilityMetadata } from '@/lib/productTourMetadata';
import { UserModel } from '@/models/UserModel';

import { Loadable } from './Loadable';

export class Chameleon extends Loadable {
  public static identify(user: UserModel, facilityType?: string): void {
    if (!user.facility_id || !Chameleon.isEnabled()) {
      return;
    }

    Chameleon.ensureScriptLoaded();
    window.chmln?.identify(
      user.id,
      Chameleon.buildIdentifyOptions(user, facilityType)
    );
  }

  public load(): void {
    if (!Chameleon.isEnabled()) {
      return;
    }

    Chameleon.ensureScriptLoaded();
  }

  private static ensureScriptLoaded(): void {
    const accountToken = import.meta.env.VITE_CHAMELEON_ACCOUNT_TOKEN;
    if (!accountToken) {
      console.warn(
        'Chameleon account token not configured. Skipping initialization.'
      );
      return;
    }

    if (
      document.querySelector(
        `script[src="https://fast.chameleon.io/messo/${accountToken}/messo.min.js"]`
      )
    ) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (function (documentObject: Document, windowObject: any, token: string) {
      var chameleonKey = 'chmln';
      var scriptElement = documentObject.createElement('script');
      if (
        (windowObject[chameleonKey] || (windowObject[chameleonKey] = {}),
        !windowObject[chameleonKey].root)
      ) {
        windowObject[chameleonKey].accountToken = token;
        windowObject[chameleonKey].location =
          windowObject.location.href.toString();
        windowObject[chameleonKey].now = new Date();
        windowObject[chameleonKey].fastUrl = 'https://fast.chameleon.io/';
        var methods =
          'identify alias track clear set show on off custom help _data'.split(
            ' '
          );
        for (var index = 0; index < methods.length; index++) {
          (function () {
            var queue: any[] = (windowObject[chameleonKey][
              methods[index]! + '_a'
            ] = []);
            windowObject[chameleonKey][methods[index]!] = function () {
              queue.push(arguments);
            };
          })();
        }
        scriptElement.src =
          windowObject[chameleonKey].fastUrl +
          'messo/' +
          token +
          '/messo.min.js';
        scriptElement.async = true;
        documentObject.head.appendChild(scriptElement);
      }
    })(document, window, accountToken);
  }

  private static buildIdentifyOptions(user: UserModel, facilityType?: string) {
    return {
      id: user.id,
      email: user.email,
      name: user.fullName,
      role: user.user_type?.title,
      user_title: user.title,
      status: user.status,
      last_sign_in_time: user.last_sign_in_time,
      facility_type: facilityType,
      organization_settings: user.organization_settings,
      ...buildProductTourFacilityMetadata(user),
      company: user.organization_details
        ? {
            uid: user.organization_details.id,
            name: user.organization_details.name
          }
        : undefined
    };
  }

  private static isEnabled(): boolean {
    return (
      Boolean(import.meta.env.VITE_CHAMELEON_ACCOUNT_TOKEN) &&
      !navigator.webdriver
    );
  }
}
